/**
 * Perfil comportamental da empresa (plano agent-first) aplicado ao Centro de
 * ações com IA (PED-12262). Fonte de verdade do valor em si é o backend
 * (`GET /company/agent-profile`, py-insights-api) — este arquivo só resolve
 * o tipo e a prioridade de exibição das pills de sugestão existentes em
 * `AGENT_THEMES` (constants/agents.ts).
 *
 * Escopo deliberadamente reduzido em relação ao protótipo de
 * `feat/test-new-interface-proposal` (agents-home-registry.ts): aqui não há
 * abas por agente nem catálogo de action cards próprio — apenas reordena os
 * shortcuts que já existem, sem alterar quantidade nem conteúdo.
 */

export type HomeProfile =
  | "team_operator"
  | "data_analyst"
  | "facilities_manager"
  | "explorer";

export const DEFAULT_PROFILE: HomeProfile = "explorer";

const VALID_PROFILES: readonly HomeProfile[] = [
  "team_operator",
  "data_analyst",
  "facilities_manager",
  "explorer",
];

/** Perfil desconhecido/nulo vindo do backend vira `explorer` silenciosamente. */
export function resolveHomeProfile(profile?: string | null): HomeProfile {
  if (!profile) return DEFAULT_PROFILE;
  return VALID_PROFILES.includes(profile as HomeProfile)
    ? (profile as HomeProfile)
    : DEFAULT_PROFILE;
}

/**
 * Prioridade dos shortcuts (por `action`, o mesmo id enviado ao agente) para
 * os 3 perfis com tema dominante. Deriva do mapeamento intent→tema do
 * backend (`INTENT_THEME_MAP` em woba-lia-datalake-step-functions):
 * members→team_operator, facilities→facilities_manager, data→data_analyst.
 */
import { REPORT_SCHEDULING_ACTION } from "@/core/components/floating-chatbot/constants/agents";

/**
 * Shortcuts sempre no topo, à frente da priorização por perfil. Reservado a
 * destaques de lançamento (pill "Novo"): são divulgação, não personalização,
 * então não devem ser empurrados para o fim por não constarem em
 * `SHORTCUT_PRIORITY`. Esvaziar quando o destaque sair do ar.
 */
const PINNED_ACTIONS: string[] = [REPORT_SCHEDULING_ACTION];

const SHORTCUT_PRIORITY: Record<Exclude<HomeProfile, "explorer">, string[]> = {
  team_operator: [
    "action_do_for_me_invite",
    "action_do_for_me",
    "importacao_membros",
    "action_do_for_me_cost_center",
    "action_do_for_me_update_plan",
    "reservas_realizar",
    "checkin_realizar",
    "Acompanhar chamado",
    "Abrir e acompanhar ordem de serviço",
    "motivos_de_uso_insights",
  ],
  facilities_manager: [
    "Acompanhar chamado",
    "Abrir e acompanhar ordem de serviço",
    "reservas_realizar",
    "checkin_realizar",
    "action_do_for_me_invite",
    "action_do_for_me",
    "importacao_membros",
    "action_do_for_me_cost_center",
    "action_do_for_me_update_plan",
    "motivos_de_uso_insights",
  ],
  data_analyst: [
    "motivos_de_uso_insights",
    "importacao_membros",
    "action_do_for_me_cost_center",
    "action_do_for_me_update_plan",
    "action_do_for_me_invite",
    "action_do_for_me",
    "reservas_realizar",
    "checkin_realizar",
    "Acompanhar chamado",
    "Abrir e acompanhar ordem de serviço",
  ],
};

/**
 * Mapeamento best-effort de intent do backend (`triage_intent`, os mesmos
 * valores que aparecem em `top_intents`) para o(s) shortcut(s) equivalentes
 * no catálogo do Centro de ações. Usado só para a priorização leve de
 * `explorer` com uso disperso (ver `buildLightPriorityFromTopIntents`).
 *
 * Intents sem correspondência direta no catálogo atual (ex.: `greeting`,
 * `out_of_scope`, `opportunity`, `strategic_advisory`, `network_telemetry`)
 * ficam de fora deliberadamente — não force um match ruim só para preencher.
 */
const INTENT_TO_SHORTCUT_ACTIONS: Record<string, string[]> = {
  invite_member: ["action_do_for_me_invite"],
  remove_member: ["action_do_for_me"],
  import_members: ["importacao_membros"],
  update_member_plan: ["action_do_for_me_update_plan"],
  update_cost_center: ["action_do_for_me_cost_center"],
  tickets_and_service_orders: [
    "Acompanhar chamado",
    "Abrir e acompanhar ordem de serviço",
  ],
  reservations: ["reservas_realizar"],
  checkin: ["checkin_realizar"],
  data_analysis: ["motivos_de_uso_insights"],
  member_insights: ["motivos_de_uso_insights"],
  graph_request: ["motivos_de_uso_insights"],
  export_data: ["motivos_de_uso_insights"],
};

/**
 * Projeta `top_intents` (já ordenado por volume no backend) nos shortcuts
 * equivalentes, preservando a ordem e removendo duplicatas. Intents sem
 * mapeamento (ver `INTENT_TO_SHORTCUT_ACTIONS`) são ignorados.
 */
function buildLightPriorityFromTopIntents(topIntents: string[]): string[] {
  const priority: string[] = [];
  for (const intent of topIntents) {
    const actions = INTENT_TO_SHORTCUT_ACTIONS[intent];
    if (!actions) continue;
    for (const action of actions) {
      if (!priority.includes(action)) priority.push(action);
    }
  }
  return priority;
}

/**
 * Resolve a lista de prioridade (ids de `shortcut.action`, em ordem) a
 * aplicar sobre as pills do Centro de ações:
 *
 * - Perfil dominante (`team_operator` / `data_analyst` / `facilities_manager`):
 *   tabela fixa do perfil (`SHORTCUT_PRIORITY`).
 * - `explorer` com uso disperso (`interactions_90d > 0` e algum
 *   `top_intents` mapeável): prioridade leve derivada de `top_intents` —
 *   cobre o cliente que já usa bastante a plataforma mas sem um tema
 *   dominante (~40%+) que justifique um dos 3 perfis fixos.
 * - `explorer` "a zero" (empresa nova, sem interações registradas ainda):
 *   lista vazia — mantém a ordem estática atual do catálogo. Não há
 *   nenhum sinal para personalizar.
 */
export function getShortcutPriority(
  profile: HomeProfile,
  topIntents: string[],
  interactions90d: number,
): string[] {
  if (profile !== "explorer") {
    return SHORTCUT_PRIORITY[profile] ?? [];
  }
  if (interactions90d > 0 && topIntents.length > 0) {
    return buildLightPriorityFromTopIntents(topIntents);
  }
  return [];
}

/**
 * Ordena itens pela lista de prioridade fornecida, usando `getAction` para
 * extrair o id de comparação de cada item (ex.: `shortcut.action`). Itens
 * fora da prioridade mantêm a ordem original, ao final. Prioridade vazia
 * retorna os itens sem alterações.
 */
export function sortByPriority<T>(
  items: T[],
  priority: string[],
  getAction: (item: T) => string,
): T[] {
  const pinnedRank = (item: T) => PINNED_ACTIONS.indexOf(getAction(item));
  const hasPinned = items.some((item) => pinnedRank(item) !== -1);

  if (!priority.length && !hasPinned) return items;

  const rank = new Map(priority.map((action, i) => [action, i]));
  return [...items].sort((a, b) => {
    // Fixos vêm antes de tudo: são destaque de lançamento, não sugestão
    // personalizada, então não competem com a prioridade por perfil.
    const pa = pinnedRank(a);
    const pb = pinnedRank(b);
    if (pa !== -1 || pb !== -1) {
      if (pa === -1) return 1;
      if (pb === -1) return -1;
      return pa - pb;
    }

    const ra = rank.get(getAction(a)) ?? priority.length;
    const rb = rank.get(getAction(b)) ?? priority.length;
    return ra - rb;
  });
}
